// Events for tracking Quiz interactions for Pop Quiz modal, based on implementation in Learner-Experience

import {ClientEvent} from '@udemy/event-tracking';

import {PopQuizFeedbackRating, PopQuizLectureType} from './types';

interface EventQuiz {
    id: number;
    numberOfQuestions: number;
}

export class QuizStarted extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number} | null,
        public attemptId: number,
    ) {
        super('QuizStarted');
    }
}

export class QuizCompleted extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number} | null,
        public attemptId: number,
        public percentCorrect: number,
    ) {
        super('QuizCompleted');
    }
}

export class PopQuizPresented extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number},
    ) {
        super('PopQuizPresented');
    }
}

export class PopQuizSnoozed extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number},
        public snoozeDurationDays: number,
    ) {
        super('PopQuizSnoozed');
    }
}

export class PopQuizContinueCourseSelected extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number},
    ) {
        super('PopQuizContinueCourseSelected');
    }
}

/**
 * Payload shape matching the shared `Assessment` field struct in schema-store
 * (`fields/Assessment.avdl`). `id` is Avro `long` there — must be a number, not the raw
 * GraphQL `ID` scalar string (e.g. "36"), or the collector rejects the event with
 * "Failed to convert JSON to Avro: Expected long. Got VALUE_STRING".
 */
export interface RecommendedAssessmentEventPayload {
    id: number;
    title: string;
    isBeta: boolean;
    domainId: string | null;
}

export class PopQuizRecommendedAssessmentPresented extends ClientEvent {
    constructor(
        public quiz: {id: number; numberOfQuestions: number},
        public course: {id: number},
        public assessment: RecommendedAssessmentEventPayload,
    ) {
        super('PopQuizRecommendedAssessmentPresented');
    }
}

export class PopQuizRecommendedAssessmentLinkSelected extends ClientEvent {
    constructor(
        public quiz: {id: number; numberOfQuestions: number},
        public course: {id: number},
        public assessment: RecommendedAssessmentEventPayload,
    ) {
        super('PopQuizRecommendedAssessmentLinkSelected');
    }
}

export class PopQuizDismissed extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number},
    ) {
        super('PopQuizDismissed');
    }
}

export class PopQuizFeedbackSubmitted extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number},
        public rating: PopQuizFeedbackRating,
    ) {
        super('PopQuizFeedbackSubmitted');
    }
}

export class PopQuizRelatedLectureLinkPresented extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number} | null,
        public lecture: {id: number; type: PopQuizLectureType},
    ) {
        super('PopQuizRelatedLectureLinkPresented');
    }
}

export class PopQuizRelatedLectureLinkSelected extends ClientEvent {
    constructor(
        public quiz: EventQuiz,
        public course: {id: number} | null,
        public lecture: {id: number; type: PopQuizLectureType},
    ) {
        super('PopQuizRelatedLectureLinkSelected');
    }
}
